/**
 * Header entry point to the Messages inbox, with a combined unread badge.
 *
 * Counts both messaging systems, because the inbox shows both: relationship-anchored 1:1
 * conversations and pod threads. Either source failing (a feature that is off, a transient error)
 * contributes 0 rather than hiding the button, so the other system stays reachable.
 *
 * This is the only global route into /messages, so it must render for every signed-in user, not
 * just coaches.
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail } from 'lucide-react'
import messagingService from '../services/messagingService'
import podMessagingService from '../services/podMessagingService'

const POLL_MS = 60000

export default function MessagesBell() {
  const navigate = useNavigate()
  const [unread, setUnread] = useState(0)

  const load = useCallback(async () => {
    const [direct, pod] = await Promise.all([
      messagingService.getUnreadCount().catch(() => null),
      podMessagingService.getUnreadCount().catch(() => 0)
    ])
    const directCount = direct?.success ? direct.unreadCount || 0 : 0
    setUnread(directCount + pod)
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, POLL_MS)
    return () => clearInterval(id)
  }, [load])

  return (
    <button
      onClick={() => navigate('/messages')}
      className="relative flex items-center p-3 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      title="Messages"
      aria-label={unread > 0 ? `Messages, ${unread} unread` : 'Messages'}
    >
      <Mail className="w-5 h-5 text-primary-500" />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  )
}
